// Щ6 — таблиците и тортата на С11, без заглавието и без тегленето на данни.
//
// Изнесени са, защото същото се показва на две места: справката зад вход
// (ReportCrops) и публичният линк. Едно копие значи, че секторите на тортата
// и колоните са едни и същи и на двете места.


// Палитрата на тортата. Същите цветове (в същия ред) са и на сървъра —
// server/utils/generate_report_pdf.py::PIE_PALETTE. Ако смениш тук, смени и там.
const PIE_PALETTE = [
    "#4e79a7", "#f28e2b", "#e15759", "#76b7b2",
    "#59a14f", "#edc948", "#b07aa1", "#ff9da7",
    "#9c755f", "#bab0ac", "#86bcb6", "#d37295",
];
const OTHER_COLOR = "#8c8c8c";   // „Други" — винаги сиво, извън палитрата
const PIE_MIN_PERCENT = 2;       // под този дял секторът се слива в „Други"
const PIE_MAX_SLICES = 11;       // + евентуален сектор „Други" = най-много 12

// Свива опашката от дребни култури в един сектор „Други". Таблицата винаги
// показва ВСИЧКИ култури — това е само за диаграмата. Идентично на
// `pie_slices` на сървъра — двете трябва да дават същите сектори.
function pieSlices(cropTotals, totalArea) {
    if (!cropTotals || !cropTotals.length || !totalArea) return [];

    const big = [], tail = [];
    cropTotals.forEach(c => {                 // вече сортирани низходящо
        if (big.length < PIE_MAX_SLICES && (c.total_area / totalArea) * 100 >= PIE_MIN_PERCENT) {
            big.push(c);
        } else {
            tail.push(c);
        }
    });
    // Ако в опашката остане само една култура — няма смисъл от „Други" с един член.
    if (tail.length === 1) big.push(tail.pop());

    const slices = big.map((c, i) => ({
        label: c.crop,
        area: c.total_area,
        percent: c.percent,
        color: PIE_PALETTE[i % PIE_PALETTE.length],
        fraction: c.total_area / totalArea,
    }));

    if (tail.length) {
        const area = tail.reduce((sum, c) => sum + c.total_area, 0);
        slices.push({
            label: `Други (${tail.length} култури)`,
            area: Math.round(area * 100) / 100,
            percent: Math.round((area / totalArea) * 100),
            color: OTHER_COLOR,
            fraction: area / totalArea,
        });
    }
    return slices;
}

// Торта с inline SVG — без външна библиотека. Секторите са <path> с дъга;
// при една-единствена култура — цял <circle>, защото дъга от 360° е израждане.
const PieChart = ({slices, size = 240}) => {
    if (!slices.length) return null;
    const r = size / 2, cx = r, cy = r;

    const sectors = [];
    let angle = -Math.PI / 2;                 // започваме от 12 часа
    slices.forEach((s, i) => {
        const sweep = s.fraction * 2 * Math.PI;
        const x1 = cx + r * Math.cos(angle);
        const y1 = cy + r * Math.sin(angle);
        const x2 = cx + r * Math.cos(angle + sweep);
        const y2 = cy + r * Math.sin(angle + sweep);
        const largeArc = sweep > Math.PI ? 1 : 0;
        sectors.push(
            <path key={i} fill={s.color} stroke="#fff" strokeWidth="1"
                  d={`M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`}/>
        );
        angle += sweep;
    });

    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}
             role="img" aria-label="Съотношение на културите">
            {slices.length === 1
                ? <circle cx={cx} cy={cy} r={r} fill={slices[0].color}/>
                : sectors}
        </svg>
    );
};


export const CropsView = ({data}) => {
    const slices = pieSlices(data.crop_totals, data.total_area);
    return (
        <>
            {!data.sections.length &&
                <div className="alert alert-info">Няма изчертани площи за тази година.</div>}

            {data.sections.map(section => (
                <div key={section.settlement_ekatte} className="mb-4">
                    <h6 className="fw-bold">{section.settlement_name}</h6>
                    <div className="table-responsive">
                        <table className="table table-sm table-bordered">
                            <thead className="tableHead">
                            <tr>
                                <th>Масив</th>
                                <th>МПО</th>
                                <th className="text-end">Площ (дка)</th>
                                <th>Култура</th>
                                {/* Ш3 */}
                                <th>Забележка</th>
                            </tr>
                            </thead>
                            <tbody>
                            {section.rows.map((row, i) => (
                                <tr key={i}>
                                    <td>{row.massif_number}</td>
                                    <td>{row.mpo_number}</td>
                                    <td className="text-end">{row.area.toFixed(2)}</td>
                                    <td>{row.crop}</td>
                                    <td>{row.note}</td>
                                </tr>
                            ))}
                            </tbody>
                            <tfoot>
                            <tr className="fw-bold">
                                <td colSpan="2">Общо</td>
                                <td className="text-end">{section.subtotal_area.toFixed(2)}</td>
                                <td></td>
                                <td></td>
                            </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            ))}

            {data.crop_totals.length > 0 && <>
                <h6 className="fw-bold mt-4">Общо по култури</h6>
                <div className="row align-items-start">
                    <div className="col-lg-7">
                        <div className="table-responsive">
                            <table className="table table-sm table-bordered">
                                <thead className="tableHead">
                                <tr>
                                    <th>Култура</th>
                                    <th className="text-end">Площ (дка)</th>
                                    <th className="text-end">%</th>
                                </tr>
                                </thead>
                                <tbody>
                                {data.crop_totals.map((row, i) => (
                                    <tr key={i}>
                                        <td>{row.crop}</td>
                                        <td className="text-end">{row.total_area.toFixed(2)}</td>
                                        <td className="text-end">{row.percent}%</td>
                                    </tr>
                                ))}
                                </tbody>
                                <tfoot>
                                <tr className="fw-bold">
                                    <td>ОБЩО</td>
                                    <td className="text-end">{data.total_area.toFixed(2)}</td>
                                    <td></td>
                                </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                    <div className="col-lg-5 d-flex flex-wrap gap-3 align-items-center">
                        <PieChart slices={slices}/>
                        <div>
                            {slices.map((s, i) => (
                                <div key={i} className="d-flex align-items-center mb-1">
                                    <span style={{
                                        display: "inline-block", width: 12, height: 12,
                                        backgroundColor: s.color, marginRight: 8, flexShrink: 0
                                    }}/>
                                    <small>{s.label} — {s.area.toFixed(2)} — {s.percent}%</small>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </>}
        </>
    );
};
